/* FADLASH anniversary display only. This component never controls discounts. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.customElements && root.HTMLElement) api.register(root);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const SAMPLE_MS = ((2 * 24 + 12) * 60 + 30) * 60000;
  const DEFAULTS = {
    scheduledText: 'Anniversary sale starts in',
    activeText: 'Selected anniversary offers end in',
    endedText: 'This anniversary offer has ended.'
  };

  // Accept only absolute ISO input. Never parse an unzoned local date string.
  function parseISO(value) {
    if (typeof value !== 'string') return null;
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:\d{2})$/);
    if (!match) return null;
    const [, year, month, day, hour, minute, second = '0', fraction = '', zone] = match;
    const parts = [Number(year), Number(month), Number(day), Number(hour), Number(minute), Number(second), Number(fraction.padEnd(3, '0'))];
    const [y, m, d, h, min, s, ms] = parts;
    if (m < 1 || m > 12 || d < 1 || d > 31 || h > 23 || min > 59 || s > 59) return null;
    let offset = 0;
    if (zone !== 'Z') {
      const oh = Number(zone.slice(1, 3));
      const om = Number(zone.slice(4, 6));
      if (oh > 23 || om > 59) return null;
      offset = (oh * 60 + om) * (zone[0] === '+' ? 1 : -1);
    }
    const wall = new Date(0);
    wall.setUTCFullYear(y, m - 1, d);
    wall.setUTCHours(h, min, s, ms);
    // Date normalizes impossible dates such as February 30; reject that rollover.
    if (wall.getUTCFullYear() !== y || wall.getUTCMonth() !== m - 1 || wall.getUTCDate() !== d) return null;
    const instant = wall.getTime() - offset * 60000;
    return Number.isFinite(instant) ? instant : null;
  }

  function validTimeZone(value) {
    if (typeof value !== 'string' || !value.trim()) return false;
    const zone = value.trim();
    // Do not guess EST/ET/PST. UTC or an explicit IANA location is required.
    if (zone !== 'UTC' && !zone.includes('/')) return false;
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: zone }).format(0);
      return true;
    } catch (_) {
      return false;
    }
  }

  function validate(config) {
    const errors = [];
    const startMs = parseISO(config.start);
    const endMs = parseISO(config.end);
    if (!String(config.eventId || '').trim()) errors.push('Add an activity identifier.');
    if (!String(config.scope || '').trim()) errors.push('Add the offer scope for this activity.');
    if (!validTimeZone(config.timeZone)) errors.push('Use a confirmed IANA time zone, such as Area/City, or UTC; do not use ET/EST.');
    if (startMs === null) errors.push('Start needs a real ISO date and time with Z or an explicit UTC offset.');
    if (endMs === null) errors.push('End needs a real ISO date and time with Z or an explicit UTC offset.');
    if (startMs !== null && endMs !== null && endMs <= startMs) errors.push('End must be later than start.');
    if (config.verified !== true) errors.push('Offer scheduling, scope and Hero copy have not been manually confirmed.');
    if (config.heroReady !== true) errors.push('A safe, activity-specific Hero end-state binding is required.');
    return { errors, startMs, endMs, timeZone: String(config.timeZone || '').trim() };
  }

  function stateAt(config, nowMs) {
    if (config.enabled !== true || config.mode === 'off') return { state: 'off' };
    if (config.mode === 'preview') return { state: 'preview', remainingMs: SAMPLE_MS };
    if (config.mode !== 'production') return { state: 'invalid', errors: ['Choose off, preview or production mode.'] };
    const checked = validate(config);
    if (!Number.isFinite(nowMs)) checked.errors.push('The current device time is unavailable.');
    if (checked.errors.length) return { state: 'invalid', errors: checked.errors };
    if (nowMs < checked.startMs) return { state: 'scheduled', remainingMs: checked.startMs - nowMs, deadlineMs: checked.startMs, timeZone: checked.timeZone };
    if (nowMs >= checked.endMs) return { state: 'ended', deadlineMs: checked.endMs, timeZone: checked.timeZone };
    return { state: 'active', remainingMs: checked.endMs - nowMs, deadlineMs: checked.endMs, timeZone: checked.timeZone };
  }

  function displayParts(remainingMs, showSeconds) {
    const unit = showSeconds ? 1000 : 60000;
    const seconds = Math.ceil(Math.max(0, remainingMs) / unit) * (showSeconds ? 1 : 60);
    const hideDays = remainingMs < 86400000;
    return {
      days: Math.floor(seconds / 86400),
      hours: hideDays ? Math.floor(seconds / 3600) : Math.floor(seconds / 3600) % 24,
      minutes: Math.floor(seconds / 60) % 60,
      seconds: seconds % 60,
      hideDays
    };
  }

  function activeLabel(config, remainingMs) {
    if (remainingMs <= 24 * 3600000) return 'Final hours — selected anniversary offers';
    const threshold = Number(config.lateHours);
    const lateHours = Number.isFinite(threshold) && threshold > 0 ? threshold : 72;
    if (remainingMs <= lateHours * 3600000) return 'Final days — selected anniversary offers';
    return String(config.activeText || DEFAULTS.activeText);
  }

  function formatTime(ms, timeZone) {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone, year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    }).format(new Date(ms)) + ' (' + timeZone + ')';
  }

  function register(env) {
    if (env.customElements.get('fadlash-countdown')) return;

    class FadlashCountdown extends env.HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;
        this._timer = null;
        this._editor = this.getAttribute('data-editor') === 'true';
        try {
          this._config = JSON.parse(this.getAttribute('data-config') || '{}');
        } catch (_) {
          this._config = { enabled: true, mode: 'invalid' };
        }
        if (!this._config || typeof this._config !== 'object' || Array.isArray(this._config)) this._config = { enabled: true, mode: 'invalid' };
        const hero = this.closest('[data-cd-hero]');
        this._promos = hero ? Array.from(hero.querySelectorAll('[data-cd-promo]')) : [];
        this._regular = hero ? Array.from(hero.querySelectorAll('[data-cd-regular]')) : [];
        this._config.heroReady = this._config.heroReady === true && Boolean(hero) && this._promos.length > 0 && this._regular.length > 0;
        this._originalHidden = new Map([...this._promos, ...this._regular].map((node) => [node, node.hidden]));
        // Read-only device time. There is deliberately no storefront test-clock API.
        this._onVisibility = () => {
          if (env.document.hidden) this._clearTimer();
          else this._refresh();
        };
        this._onPageShow = () => this._refresh();
        env.document.addEventListener('visibilitychange', this._onVisibility);
        env.addEventListener('pageshow', this._onPageShow);
        this._refresh();
      }

      disconnectedCallback() {
        if (!this._connected) return;
        this._connected = false;
        this._clearTimer();
        env.document.removeEventListener('visibilitychange', this._onVisibility);
        env.removeEventListener('pageshow', this._onPageShow);
        this._restoreHero();
        this._promos = [];
        this._regular = [];
        this._originalHidden = null;
      }

      _clearTimer() {
        if (this._timer !== null && this._timer !== undefined) env.clearTimeout(this._timer);
        this._timer = null;
      }

      _restoreHero() {
        if (this._originalHidden) this._originalHidden.forEach((hidden, node) => { node.hidden = hidden; });
      }

      _node(name) { return this.querySelector('[data-cd-' + name + ']'); }

      _show(name, visible) {
        const node = this._node(name);
        if (node) node.hidden = !visible;
      }

      _text(name, text) {
        const node = this._node(name);
        if (node && node.textContent !== String(text)) node.textContent = String(text);
      }

      _refresh() {
        if (!this._connected) return;
        this._clearTimer();
        const config = this._config;
        const current = stateAt(config, env.Date.now());
        this.dataset.state = current.state;
        this._applyHero(current.state);
        this._render(current);
        if ((current.state === 'scheduled' || current.state === 'active') && !env.document.hidden) {
          // Recompute from the absolute deadline every time; never decrement cached time.
          this._timer = env.setTimeout(() => this._refresh(), Math.max(1, Math.min(1000, current.remainingMs)));
        }
      }

      _applyHero(state) {
        if (this._config.mode !== 'production' || state === 'off') {
          this._restoreHero();
          return;
        }
        const active = state === 'active';
        this._promos.forEach((node) => { node.hidden = !active; });
        this._regular.forEach((node) => { node.hidden = active; });
      }

      _render(current) {
        const config = this._config;
        const state = current.state;
        const preview = state === 'preview';
        const timed = state === 'scheduled' || state === 'active';
        const visible = preview || (timed && (state !== 'scheduled' || config.showWarmup === true)) || (state === 'ended' && config.endBehavior !== 'hide');
        this.hidden = !visible && !(state === 'invalid' && this._editor);
        this._show('content', visible);
        this._show('error', state === 'invalid' && this._editor);
        this._text('error', state === 'invalid' ? current.errors.join(' ') : '');
        this._show('preview-note', preview);
        this._text('preview-note', 'Preview only — sample countdown. Dates and offers pending.');
        this._show('digits', preview || timed);
        this._show('deadline', timed);
        this._text('scope', String(config.scope || 'Selected anniversary offers only.'));

        if (preview || timed) {
          const parts = displayParts(current.remainingMs, config.showSeconds === true);
          ['days', 'hours', 'minutes', 'seconds'].forEach((key) => this._text(key, String(parts[key]).padStart(2, '0')));
          this._show('days-group', !parts.hideDays);
          this._show('seconds-group', config.showSeconds === true);
        }
        if (preview) this._text('heading', 'Anniversary countdown preview');
        if (state === 'scheduled') this._text('heading', config.scheduledText || DEFAULTS.scheduledText);
        if (state === 'active') this._text('heading', activeLabel(config, current.remainingMs));
        if (state === 'ended') this._text('heading', config.endedText || DEFAULTS.endedText);
        if (timed) this._text('deadline', (state === 'scheduled' ? 'Starts ' : 'Ends ') + formatTime(current.deadlineMs, current.timeZone) + '.');

        this._show('editor-conversion', this._editor && state !== 'off');
        if (this._editor && state !== 'off') {
          const start = parseISO(config.start);
          const end = parseISO(config.end);
          const zone = String(config.timeZone || '').trim();
          this._text('editor-conversion', start !== null && end !== null && validTimeZone(zone)
            ? 'Manual timing check: start ' + formatTime(start, zone) + ' = ' + new Date(start).toISOString() + '; end ' + formatTime(end, zone) + ' = ' + new Date(end).toISOString() + '. This does not verify Shopify discounts.'
            : 'Activity dates and time zone are pending. Production stays unavailable until valid dates, Hero binding and the manual offer check are complete.');
        }
      }
    }
    env.customElements.define('fadlash-countdown', FadlashCountdown);
  }

  return { parseISO, validTimeZone, validate, stateAt, displayParts, activeLabel, formatTime, register };
});
